import { MAX_SELECTABLE_SERIES } from "~/feature/chart/constants";
import {
  getGroupedMarketIdsSelection,
  toggleMarketSelection,
} from "~/feature/chart/utils";
import { isConsensusSelection } from "~/feature/chart/utils/isConsensusSelection";
import type { IEmbedSize } from "~/feature/embed/utils/getEmbedMinSize";
import type { IMarket } from "~/types/event";

import { defaultEmbedOptions } from "../constants";
import { EmbedOptionEnum, type EmbedOptionsState } from "../types";

export interface IEmbedState {
  selections: string[];
  options: EmbedOptionsState;
  size: IEmbedSize | null;
}

export type EmbedAction =
  | { type: "toggleMarket"; marketId: string; markets: IMarket[] }
  | { type: "selectConsensus"; marketId: string; markets: IMarket[] }
  | { type: "setOption"; option: EmbedOptionEnum; value: boolean }
  | { type: "setSize"; size: IEmbedSize };

export const createEmbedState = (selections: string[]): IEmbedState => ({
  selections,
  options: defaultEmbedOptions,
  size: null,
});

// "Consensus only" is a property of a consensus selection; once the selection
// stops being one the toggle disappears, and it must not come back already on.
const withSelections = (
  state: IEmbedState,
  selections: string[],
  markets: IMarket[]
): IEmbedState => ({
  ...state,
  selections,
  options:
    state.options[EmbedOptionEnum.CONSENSUS_ONLY] &&
    !isConsensusSelection(markets, selections)
      ? { ...state.options, [EmbedOptionEnum.CONSENSUS_ONLY]: false }
      : state.options,
});

export const embedReducer = (
  state: IEmbedState,
  action: EmbedAction
): IEmbedState => {
  switch (action.type) {
    case "toggleMarket":
      return withSelections(
        state,
        toggleMarketSelection(state.selections, action.marketId),
        action.markets
      );
    case "selectConsensus":
      return withSelections(
        state,
        getGroupedMarketIdsSelection(action.markets, action.marketId).slice(
          0,
          MAX_SELECTABLE_SERIES
        ),
        action.markets
      );
    case "setOption":
      return {
        ...state,
        options: { ...state.options, [action.option]: action.value },
      };
    case "setSize":
      return { ...state, size: action.size };
  }
};
